import React from 'react'
import { router } from '@inertiajs/react'
import { Loader2, ChevronRight } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import { formatNumber, formatDuration } from '@/frontend/pages/CommandCenter/Telemetry/utils'
import { ConversationView } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import { AllEventsView } from '@/frontend/pages/CommandCenter/shared/AllEventsView'
import TokensWithBreakdown from '@/frontend/pages/CommandCenter/Telemetry/components/TokensWithBreakdown'
import SessionHeader from '@/frontend/pages/CommandCenter/Telemetry/Sessions/SessionHeader'
import SessionEvalPanel, { type SessionEvalVerdict } from '@/frontend/components/Eval/SessionEvalPanel'
import EvalVerdictDetails from '@/frontend/components/Eval/EvalVerdictDetails'
import type { FleetSessionEvent } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import type { TokenBreakdown } from '@/frontend/pages/CommandCenter/Telemetry/types'

interface FleetSession {
  id: string
  sessionId: string
  label: string | null
  lastChannel: string | null
  model: string | null
  totalEvents: number
  totalTokens: number
  startedAt: string | null
  lastEventAt: string | null
  tokenBreakdown: TokenBreakdown | null
}

interface Attendance {
  index: number | null
  label: string
  startedAt: string | null
  endedAt: string | null
  kind: string
  problem: boolean
  severity: string | null
  verdict: SessionEvalVerdict | null
}

interface Props {
  session: FleetSession
  digitalWorker: { id: string; name: string; instanceName: string }
  organization: { id: string; name: string; slug: string }
  events: FleetSessionEvent[]
  pagination: { page: number; totalPages: number; total: number }
  role: string
  viewMode: string
  showThinking: boolean
  showToolCalls: boolean
  showToolResults: boolean
  evalVerdict: SessionEvalVerdict | null
  attendances: Attendance[]
  anchorAttendance: number | null
}

// Cycling palette so adjacent attendances never share a color. Full literal
// class strings (no concatenation) so Tailwind keeps them at build time.
const SEG_PALETTE = [
  { acc: 'border-l-sky-400', dot: 'bg-sky-400', chip: 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-200', edge: 'border-sky-200 dark:border-sky-900' },
  { acc: 'border-l-violet-400', dot: 'bg-violet-400', chip: 'bg-violet-50 text-violet-800 dark:bg-violet-950 dark:text-violet-200', edge: 'border-violet-200 dark:border-violet-900' },
  { acc: 'border-l-amber-400', dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200', edge: 'border-amber-200 dark:border-amber-900' },
  { acc: 'border-l-emerald-400', dot: 'bg-emerald-400', chip: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200', edge: 'border-emerald-200 dark:border-emerald-900' },
  { acc: 'border-l-rose-400', dot: 'bg-rose-400', chip: 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200', edge: 'border-rose-200 dark:border-rose-900' },
  { acc: 'border-l-cyan-400', dot: 'bg-cyan-400', chip: 'bg-cyan-50 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200', edge: 'border-cyan-200 dark:border-cyan-900' },
]

const fmtTime = (s: string) =>
  new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

function fmtRange(a?: string | null, b?: string | null) {
  if (!a) return ''
  const end = b && new Date(b).getTime() !== new Date(a).getTime() ? fmtTime(b) : null
  return end ? `${fmtTime(a)} – ${end}` : fmtTime(a)
}

// Index into `attendances` whose [start, end] window contains the event, or -1
// (activity between attendances / not yet judged). attendances are sorted asc.
function attendanceAt(tIso: string, atts: Attendance[]): number {
  const t = new Date(tIso).getTime()
  for (let i = 0; i < atts.length; i++) {
    const s = atts[i].startedAt ? new Date(atts[i].startedAt as string).getTime() : Number.POSITIVE_INFINITY
    const e = atts[i].endedAt ? new Date(atts[i].endedAt as string).getTime() : s
    if (t >= s && t <= e) return i
  }
  return -1
}

// One attendance's collapsed eval. A real <button> so it's keyboard-reachable
// (the only control exposing the verdict in segmented sessions); on expand it
// scrolls its verdict into view so it isn't hidden above a stuck sticky header.
function AttendanceAccordion({ att, colorIdx, open, onToggle, sticky = false }: {
  att: Attendance
  colorIdx: number
  open: boolean
  onToggle: () => void
  sticky?: boolean
}) {
  const verdictRef = React.useRef<HTMLDivElement>(null)
  const wasOpen = React.useRef(open)
  React.useEffect(() => {
    // Only on the user's expand (false→true), not on every mount/re-render where
    // it's already open (prepending older pages must not yank the scroll).
    if (open && !wasOpen.current) verdictRef.current?.scrollIntoView({ block: 'nearest' })
    wasOpen.current = open
  }, [open])
  const c = SEG_PALETTE[colorIdx % SEG_PALETTE.length]
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`${sticky ? 'sticky top-0 z-20' : ''} -ml-2 mb-1 flex w-full cursor-pointer flex-wrap items-center gap-2 border-y px-2 py-1 text-left text-xs backdrop-blur ${c.edge} ${c.chip}`}
      >
        <ChevronRight className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className={`h-2 w-2 shrink-0 rounded-full ${c.dot}`} />
        <span className="font-semibold">{att.label}</span>
        <span className="opacity-70">{fmtRange(att.startedAt, att.endedAt)}</span>
        {att.problem && <span className="rounded bg-red-600 px-1 py-0.5 text-[10px] font-semibold uppercase text-white">problema</span>}
        {att.kind === 'qa' && <span className="rounded bg-blue-600 px-1 py-0.5 text-[10px] uppercase text-white">qa</span>}
      </button>
      {open && att.verdict && (
        <div ref={verdictRef} className="mb-1 scroll-mt-10 rounded-md border bg-card px-3 py-2">
          <EvalVerdictDetails verdict={att.verdict} />
        </div>
      )}
    </>
  )
}

function SessionShow({ session, digitalWorker, events, pagination, role, viewMode, showThinking, showToolCalls, showToolResults, evalVerdict, attendances, anchorAttendance }: Props) {
  const isConversation = viewMode === 'conversation'
  const basePath = `/cc/telemetry/digital_workers/${digitalWorker.id}/sessions/${encodeURIComponent(session.sessionId)}`

  // Lazy load: page 1 (newest) renders first; older pages stream in as the user
  // scrolls toward the history edge — no aggressive pagination, no full load.
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const edgeRef = React.useRef<HTMLDivElement>(null)
  const pageRef = React.useRef(1)
  const loadingRef = React.useRef(false)
  const anchorTriesRef = React.useRef(0)
  const anchorDoneRef = React.useRef(false)
  const [items, setItems] = React.useState<FleetSessionEvent[]>(events)
  const [loading, setLoading] = React.useState(false)
  const [openAttendances, setOpenAttendances] = React.useState<Set<number>>(new Set())
  const hasMore = pageRef.current < pagination.totalPages

  // A session has per-attendance verdicts (≥2) regardless of the view — used to
  // suppress the single top panel everywhere. Only the conversation view renders
  // the per-attendance banding/accordions.
  const hasAttendances = attendances.filter((a) => a.index !== null).length >= 2
  const segmented = isConversation && hasAttendances
  const toggleAttendance = (i: number) =>
    setOpenAttendances((prev) => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })

  // Reset when filters change (Inertia reloads page 1) and land at the reading
  // edge: bottom for the chat, top for the descending log. When arriving from a
  // specific attendance, the anchor effect positions instead.
  React.useEffect(() => {
    setItems(events)
    pageRef.current = 1
    anchorTriesRef.current = 0
    anchorDoneRef.current = false
    // Only let the anchor effect own the scroll when it can actually anchor
    // (a segmented page with the requested attendance). Otherwise fall back to
    // the normal reading edge so the link doesn't strand the reader at the top.
    if (anchorAttendance != null && segmented) return
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = isConversation ? el.scrollHeight : 0
    })
  }, [events, isConversation, anchorAttendance, segmented])

  const loadOlder = React.useCallback(async () => {
    if (loadingRef.current || pageRef.current >= pagination.totalPages) return
    loadingRef.current = true
    setLoading(true)
    const el = scrollRef.current
    const prevHeight = el ? el.scrollHeight : 0
    const prevTop = el ? el.scrollTop : 0
    try {
      const qs = new URLSearchParams({ page: String(pageRef.current + 1), view_mode: viewMode })
      if (role) qs.set('role', role)
      if (showToolResults) qs.set('show_tool_results', 'true')
      const res = await fetch(`${basePath}/events?${qs}`, { headers: { Accept: 'application/json' } })
      if (!res.ok) return
      const data: { events: FleetSessionEvent[] } = await res.json()
      pageRef.current += 1
      if (isConversation) {
        setItems((prev) => [...data.events, ...prev]) // older on top
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight + prevTop // keep visual position
        })
      } else {
        setItems((prev) => [...prev, ...data.events]) // descending log: older at the bottom
      }
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [basePath, viewMode, role, showToolResults, isConversation, pagination.totalPages])

  React.useEffect(() => {
    const el = scrollRef.current
    const sentinel = edgeRef.current
    if (!el || !sentinel) return
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadOlder()
    }, { root: el, rootMargin: '300px', threshold: 0 })
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [loadOlder])

  // Arriving from an attendance verdict: scroll to that attendance ONCE,
  // pulling older pages until its events are loaded (bounded by totalPages).
  // After the first successful scroll we stop, so later lazy-loads (the reader
  // scrolling up for context) don't snap the view back to the anchor.
  React.useEffect(() => {
    if (anchorAttendance == null || !segmented || anchorDoneRef.current) return
    const el = scrollRef.current
    if (!el) return
    const target = el.querySelector(`#att-${anchorAttendance}`) as HTMLElement | null
    // The attendance may span more than one page: its div can exist while only
    // its newest events are loaded. Wait until the oldest loaded event reaches
    // the attendance start, so scrolling lands on its beginning, not mid-way.
    const att = attendances.find((a) => a.index === anchorAttendance)
    const startLoaded = !att?.startedAt || (items.length > 0 &&
      new Date(items[0].timestamp).getTime() <= new Date(att.startedAt).getTime())
    if (target && startLoaded) {
      target.scrollIntoView({ block: 'start' })
      anchorDoneRef.current = true
    } else if (pageRef.current < pagination.totalPages && anchorTriesRef.current < pagination.totalPages) {
      anchorTriesRef.current += 1
      loadOlder()
    }
  }, [anchorAttendance, segmented, items, attendances, loadOlder, pagination.totalPages])

  // Split the chronological event list into contiguous runs by attendance so
  // each run can carry its own sticky colored header.
  const groups = React.useMemo(() => {
    if (!segmented) return null
    const res: { attIdx: number; events: FleetSessionEvent[] }[] = []
    for (const ev of items) {
      const ai = attendanceAt(ev.timestamp, attendances)
      const last = res[res.length - 1]
      if (last && last.attIdx === ai) last.events.push(ev)
      else res.push({ attIdx: ai, events: [ev] })
    }
    return res
  }, [items, attendances, segmented])

  // Attendance array-indices already rendered inline (their events matched a
  // window). Each attendance NOT here — the "all events" view, or windows that
  // couldn't be matched to events (e.g. missing timestamps) — is listed in the
  // fallback individually, so no single verdict is dropped.
  const shownInline = new Set(
    isConversation && groups ? groups.filter((g) => g.attIdx >= 0).map((g) => g.attIdx) : []
  )
  const fallbackAttendances = attendances
    .map((a, i) => ({ att: a, colorIdx: i }))
    .filter((x) => x.att.index !== null && !shownInline.has(x.colorIdx))

  const navigateWithParams = (overrides: Record<string, string | number | undefined>) => {
    const params: Record<string, string | number | undefined> = {
      view_mode: viewMode,
      role: role || undefined,
      show_thinking: showThinking ? undefined : 'false',
      show_tool_calls: showToolCalls ? undefined : 'false',
      show_tool_results: showToolResults ? 'true' : undefined,
      ...overrides,
    }
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )
    router.get(basePath, cleanParams, { preserveState: true })
  }

  const spansMultipleDays = items.length >= 2 && (() => {
    const first = new Date(items[0].timestamp)
    const last = new Date(items[items.length - 1].timestamp)
    return first.toDateString() !== last.toDateString()
  })()

  const duration = formatDuration(session.startedAt, session.lastEventAt)
  const statItems = [
    <span key="events">{formatNumber(session.totalEvents)} eventos</span>,
    <span key="tokens">
      <TokensWithBreakdown total={session.totalTokens} breakdown={session.tokenBreakdown} />
      {' '}tokens
    </span>,
    duration !== '—' ? <span key="duration">{duration}</span> : null,
    session.lastChannel ? <span key="channel">{session.lastChannel}</span> : null,
    session.model ? <span key="model">{session.model}</span> : null,
  ].filter(Boolean)

  const loadingBar = (
    <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> carregando mensagens mais antigas…
    </div>
  )

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-4 pt-4">
        <SessionHeader
          sessionLabel={session.label}
          sessionId={session.sessionId}
          digitalWorkerId={digitalWorker.id}
          isConversation={isConversation}
          role={role}
          showThinking={showThinking}
          showToolCalls={showToolCalls}
          showToolResults={showToolResults}
          onNavigate={navigateWithParams}
          currentPage={1}
        />
        <p className="mb-3 ml-8 text-xs text-muted-foreground">
          {statItems.map((item, i) => <React.Fragment key={i}>{i > 0 && ' · '}{item}</React.Fragment>)}
        </p>
        {/* Segmented sessions show the verdict per attendance below; only the
            single-verdict (non-segmented) case keeps the top panel. */}
        {!hasAttendances && evalVerdict && <SessionEvalPanel verdict={evalVerdict} digitalWorkerId={digitalWorker.id} />}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-6">
        {isConversation && <div ref={edgeRef} />}
        {isConversation && hasMore && loading && loadingBar}
        {/* Verdicts not shown inline (all-events view, or attendances whose windows
            didn't match events) are listed here so none is dropped — AFTER the
            edgeRef so the load-older sentinel stays at the top edge. */}
        {fallbackAttendances.length > 0 && (
          <div className="mb-4">
            {fallbackAttendances.map(({ att, colorIdx }) => (
              // id={att-N} so ?atend=N links (eval ledger / Slack) still anchor to
              // attendances that only appear here.
              <div key={att.index} id={`att-${att.index}`}>
                <AttendanceAccordion
                  att={att}
                  colorIdx={colorIdx}
                  open={att.index != null && openAttendances.has(att.index)}
                  onToggle={() => att.index != null && toggleAttendance(att.index)}
                />
              </div>
            ))}
          </div>
        )}

        {!isConversation ? (
          <AllEventsView events={items} spansMultipleDays={spansMultipleDays} showThinking={showThinking} showToolCalls={showToolCalls} />
        ) : groups ? (
          groups.map((g, gi) => {
            if (g.attIdx < 0) {
              return (
                <div key={gi} className="border-l-2 border-l-transparent pl-2">
                  {gi > 0 && (
                    <div className="sticky top-0 z-10 my-1 flex items-center justify-center py-0.5">
                      <span className="rounded-full bg-muted/80 px-2 py-0.5 text-[10px] text-muted-foreground backdrop-blur">· entre atendimentos ·</span>
                    </div>
                  )}
                  <ConversationView events={g.events} sessionModel={session.model} spansMultipleDays={spansMultipleDays} showThinking={showThinking} showToolCalls={showToolCalls} />
                </div>
              )
            }
            const att = attendances[g.attIdx]
            const c = SEG_PALETTE[g.attIdx % SEG_PALETTE.length]
            return (
              <div key={gi} id={`att-${att.index}`} className={`border-l-2 ${c.acc} pl-2`}>
                <AttendanceAccordion
                  att={att}
                  colorIdx={g.attIdx}
                  open={att.index != null && openAttendances.has(att.index)}
                  onToggle={() => att.index != null && toggleAttendance(att.index)}
                  sticky
                />
                <ConversationView events={g.events} sessionModel={session.model} spansMultipleDays={spansMultipleDays} showThinking={showThinking} showToolCalls={showToolCalls} />
              </div>
            )
          })
        ) : (
          <ConversationView
            events={items}
            sessionModel={session.model}
            spansMultipleDays={spansMultipleDays}
            showThinking={showThinking}
            showToolCalls={showToolCalls}
          />
        )}

        {items.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">Nenhum evento encontrado.</div>
        )}

        {!isConversation && hasMore && loading && loadingBar}
        {!isConversation && <div ref={edgeRef} />}
      </div>
    </div>
  )
}

SessionShow.layout = (page: React.ReactElement) => <CommandCenterLayout variant="fullscreen">{page}</CommandCenterLayout>

export default SessionShow
